import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CRITICAL_SAFETY_RULES } from '../data/criticalSafetyRules';

// ── Medication categories with safety-relevant items ──

interface MedicationItem {
  id: string;
  label: string;
  /** Lowercase terms for matching against critical safety rules */
  matchTerms: string[];
}

interface MedicationCategory {
  id: string;
  name: string;
  description: string;
  items: MedicationItem[];
}

const MEDICATION_CATEGORIES: MedicationCategory[] = [
  {
    id: 'blood-thinners',
    name: 'Blood Thinners & Anticoagulants',
    description: 'Medications that prevent blood clots or thin the blood.',
    items: [
      { id: 'warfarin', label: 'Warfarin (Coumadin)', matchTerms: ['warfarin', 'coumadin'] },
      { id: 'heparin', label: 'Heparin / Enoxaparin (Lovenox)', matchTerms: ['heparin', 'enoxaparin', 'lovenox'] },
      { id: 'aspirin-blood', label: 'Aspirin (daily, for blood thinning)', matchTerms: ['aspirin'] },
      { id: 'apixaban', label: 'Apixaban (Eliquis)', matchTerms: ['apixaban', 'eliquis'] },
      { id: 'rivaroxaban', label: 'Rivaroxaban (Xarelto)', matchTerms: ['rivaroxaban', 'xarelto'] },
      { id: 'clopidogrel', label: 'Clopidogrel (Plavix)', matchTerms: ['clopidogrel', 'plavix'] },
    ],
  },
  {
    id: 'diabetes',
    name: 'Diabetes Medications',
    description: 'Insulin and oral medications for blood sugar management.',
    items: [
      { id: 'metformin', label: 'Metformin', matchTerms: ['metformin'] },
      { id: 'insulin', label: 'Insulin (any type)', matchTerms: ['insulin'] },
      { id: 'glipizide', label: 'Glipizide / Glyburide', matchTerms: ['glipizide', 'glyburide'] },
      { id: 'semaglutide', label: 'Semaglutide (Ozempic, Wegovy)', matchTerms: ['semaglutide', 'ozempic', 'wegovy'] },
      { id: 'tirzepatide', label: 'Tirzepatide (Mounjaro)', matchTerms: ['tirzepatide', 'mounjaro'] },
    ],
  },
  {
    id: 'heart-bp',
    name: 'Heart & Blood Pressure Medications',
    description: 'Medications for heart conditions, chest pain, and blood pressure.',
    items: [
      { id: 'nitroglycerin', label: 'Nitroglycerin / Nitrates (Imdur, Isordil)', matchTerms: ['nitroglycerin', 'isosorbide mononitrate', 'isosorbide dinitrate', 'nitrates', 'nitro', 'imdur', 'isordil'] },
      { id: 'lisinopril', label: 'Lisinopril / Enalapril (ACE Inhibitors)', matchTerms: ['lisinopril', 'enalapril'] },
      { id: 'amlodipine', label: 'Amlodipine (Norvasc)', matchTerms: ['amlodipine', 'norvasc'] },
      { id: 'metoprolol', label: 'Metoprolol / Atenolol (Beta Blockers)', matchTerms: ['metoprolol', 'atenolol'] },
      { id: 'losartan', label: 'Losartan / Valsartan (ARBs)', matchTerms: ['losartan', 'valsartan'] },
    ],
  },
  {
    id: 'mental-health',
    name: 'Mental Health Medications',
    description: 'Antidepressants, anti-anxiety, and mood stabilizers.',
    items: [
      { id: 'ssri', label: 'SSRIs (Zoloft, Prozac, Lexapro, Celexa, Paxil)', matchTerms: ['sertraline', 'fluoxetine', 'paroxetine', 'citalopram', 'escitalopram', 'ssri', 'zoloft', 'prozac', 'paxil', 'lexapro', 'celexa'] },
      { id: 'snri', label: 'SNRIs (Effexor, Cymbalta, Pristiq)', matchTerms: ['venlafaxine', 'duloxetine', 'desvenlafaxine', 'snri', 'effexor', 'cymbalta', 'pristiq'] },
      { id: 'maoi', label: 'MAOIs (Nardil, Parnate, Marplan)', matchTerms: ['phenelzine', 'tranylcypromine', 'isocarboxazid', 'selegiline', 'maoi', 'nardil', 'parnate', 'marplan', 'emsam'] },
      { id: 'benzodiazepine', label: 'Benzodiazepines (Xanax, Klonopin, Ativan)', matchTerms: ['alprazolam', 'clonazepam', 'lorazepam', 'diazepam', 'xanax', 'klonopin', 'ativan', 'valium'] },
      { id: 'lithium', label: 'Lithium', matchTerms: ['lithium'] },
    ],
  },
  {
    id: 'pain-opioids',
    name: 'Pain & Opioid Medications',
    description: 'Prescription pain relievers including opioids.',
    items: [
      { id: 'opioid', label: 'Opioids (Oxycodone, Hydrocodone, Morphine, Fentanyl)', matchTerms: ['oxycodone', 'hydrocodone', 'morphine', 'fentanyl', 'codeine', 'tramadol', 'opioid', 'opioids', 'percocet', 'vicodin', 'norco'] },
      { id: 'buprenorphine', label: 'Buprenorphine / Methadone (Suboxone, Subutex)', matchTerms: ['methadone', 'buprenorphine', 'suboxone', 'subutex'] },
      { id: 'gabapentin', label: 'Gabapentin / Pregabalin (Neurontin, Lyrica)', matchTerms: ['gabapentin', 'pregabalin', 'neurontin', 'lyrica'] },
    ],
  },
  {
    id: 'hormones',
    name: 'Hormone Therapy',
    description: 'Hormone replacement, thyroid, and related treatments.',
    items: [
      { id: 'levothyroxine', label: 'Levothyroxine (Synthroid)', matchTerms: ['levothyroxine', 'synthroid'] },
      { id: 'estradiol', label: 'Estradiol / Estrogen (HRT)', matchTerms: ['estradiol', 'estrogen'] },
      { id: 'progesterone', label: 'Progesterone (Prometrium)', matchTerms: ['progesterone', 'prometrium'] },
      { id: 'testosterone', label: 'Testosterone (TRT)', matchTerms: ['testosterone'] },
      { id: 'finasteride', label: 'Finasteride / Dutasteride', matchTerms: ['finasteride', 'dutasteride'] },
    ],
  },
];

// ── Safety exclusion detection ──

interface SafetyAlert {
  ruleId: string;
  ruleName: string;
  severity: 'block' | 'warn' | 'info';
  message: string;
}

function detectSafetyExclusions(selectedMedIds: string[]): SafetyAlert[] {
  // Collect all matchTerms from selected medications
  const allTerms: string[] = [];
  for (const cat of MEDICATION_CATEGORIES) {
    for (const item of cat.items) {
      if (selectedMedIds.includes(item.id)) {
        allTerms.push(...item.matchTerms);
      }
    }
  }

  if (allTerms.length === 0) return [];

  const alerts: SafetyAlert[] = [];
  for (const rule of CRITICAL_SAFETY_RULES) {
    if (!rule.isActive) continue;
    if (rule.triggerType !== 'drug_interaction') continue;
    if (!rule.matchMedications) continue;

    const matched = rule.matchMedications.some((med) =>
      allTerms.some((term) => term.toLowerCase() === med.toLowerCase()),
    );

    if (matched) {
      alerts.push({
        ruleId: rule.id,
        ruleName: rule.name,
        severity: rule.severity as 'block' | 'warn' | 'info',
        message: rule.message,
      });
    }
  }

  return alerts;
}

// ── Component ──

export function CurrentMedicationsModal() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [freeText, setFreeText] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(MEDICATION_CATEGORIES.map((c) => c.id)),
  );

  const safetyAlerts = useMemo(
    () => detectSafetyExclusions(Array.from(selected)),
    [selected],
  );

  function toggleMed(medId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(medId)) next.delete(medId);
      else next.add(medId);
      return next;
    });
  }

  function toggleCategory(catId: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }

  function handleSubmit() {
    const selectedLabels: string[] = [];
    for (const cat of MEDICATION_CATEGORIES) {
      for (const item of cat.items) {
        if (selected.has(item.id)) {
          selectedLabels.push(item.label);
        }
      }
    }
    // Include free-text entries
    const freeEntries = freeText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const allMeds = [...selectedLabels, ...freeEntries];

    // Store in sessionStorage for downstream consumption
    sessionStorage.setItem(
      'hemologix_current_medications',
      JSON.stringify({
        medications: allMeds,
        safetyAlerts: safetyAlerts,
        submittedAt: new Date().toISOString(),
      }),
    );

    navigate('/app');
  }

  function handleNone() {
    sessionStorage.setItem(
      'hemologix_current_medications',
      JSON.stringify({
        medications: [],
        safetyAlerts: [],
        submittedAt: new Date().toISOString(),
      }),
    );
    navigate('/app');
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#FAFAFA',
        fontFamily: "'DM Sans', -apple-system, sans-serif",
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <h1
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 24,
            fontWeight: 400,
            color: '#18181B',
            marginBottom: 4,
          }}
        >
          Current Medications
        </h1>
        <p
          style={{
            fontSize: 15,
            color: '#52525B',
            marginBottom: 24,
            lineHeight: 1.5,
          }}
        >
          Select any medications you are currently taking. This helps us check
          for potential interactions and personalize your protocol safely.
        </p>

        {/* Safety alerts */}
        {safetyAlerts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {safetyAlerts.map((alert) => (
              <div
                key={alert.ruleId}
                style={{
                  background: alert.severity === 'block' ? 'rgba(220,38,38,0.08)' : 'rgba(202,138,4,0.08)',
                  border: `1px solid ${alert.severity === 'block' ? '#DC2626' : '#CA8A04'}`,
                  borderRadius: 6,
                  padding: '12px 16px',
                  marginBottom: 8,
                }}
              >
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: alert.severity === 'block' ? '#991B1B' : '#92400E',
                    marginBottom: 4,
                  }}
                >
                  {alert.severity === 'block' ? 'Safety Block' : 'Warning'}: {alert.ruleName}
                </p>
                <p
                  style={{
                    fontSize: 13,
                    color: alert.severity === 'block' ? '#991B1B' : '#92400E',
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {alert.message}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Medication categories */}
        {MEDICATION_CATEGORIES.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const selectedCount = category.items.filter((item) =>
            selected.has(item.id),
          ).length;

          return (
            <div
              key={category.id}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E4E4E7',
                borderRadius: 6,
                marginBottom: 12,
                overflow: 'hidden',
              }}
            >
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#18181B',
                      margin: 0,
                    }}
                  >
                    {category.name}
                    {selectedCount > 0 && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 13,
                          fontWeight: 500,
                          color: '#E11D48',
                        }}
                      >
                        ({selectedCount})
                      </span>
                    )}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: '#52525B',
                      margin: '2px 0 0 0',
                      lineHeight: 1.4,
                    }}
                  >
                    {category.description}
                  </p>
                </div>
                <span
                  style={{
                    fontSize: 18,
                    color: '#52525B',
                    flexShrink: 0,
                    marginLeft: 12,
                    transition: 'transform 0.2s ease',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▾
                </span>
              </button>

              {/* Category items */}
              {isExpanded && (
                <div
                  style={{
                    borderTop: '1px solid #E4E4E7',
                    padding: '8px 0',
                  }}
                >
                  {category.items.map((item) => {
                    const isChecked = selected.has(item.id);
                    return (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 16px',
                          cursor: 'pointer',
                          background: isChecked
                            ? 'rgba(225,29,72,0.05)'
                            : 'transparent',
                          transition: 'background 0.15s ease',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleMed(item.id)}
                          style={{
                            width: 18,
                            height: 18,
                            accentColor: '#E11D48',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        />
                        <span
                          style={{
                            fontSize: 14,
                            color: '#18181B',
                            lineHeight: 1.4,
                          }}
                        >
                          {item.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Free-text field */}
        <div
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: 6,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 600,
              color: '#18181B',
              marginBottom: 4,
            }}
          >
            Other medications not listed above
          </label>
          <p
            style={{
              fontSize: 13,
              color: '#52525B',
              margin: '0 0 10px 0',
              lineHeight: 1.4,
            }}
          >
            Separate multiple medications with commas.
          </p>
          <textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="e.g., Levothyroxine, Vitamin D, Magnesium"
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: 14,
              fontFamily: "'DM Sans', -apple-system, sans-serif",
              color: '#18181B',
              background: '#FAFAFA',
              border: '1px solid #E4E4E7',
              borderRadius: 4,
              resize: 'vertical',
              outline: 'none',
              lineHeight: 1.5,
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#E11D48';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#E4E4E7';
            }}
          />
          <p
            style={{
              fontSize: 13,
              color: '#52525B',
              margin: '8px 0 0 0',
              lineHeight: 1.4,
            }}
          >
            Free-text medications are reviewed by our safety system during
            protocol generation. If an interaction is detected, it will be
            flagged before anything ships.
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <button
            onClick={handleNone}
            style={{
              flex: 1,
              padding: '14px 16px',
              fontSize: 15,
              fontWeight: 500,
              color: '#18181B',
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#E11D48';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E4E4E7';
            }}
          >
            I take no medications
          </button>
          <button
            onClick={handleSubmit}
            disabled={selected.size === 0 && !freeText.trim()}
            style={{
              flex: 1,
              padding: '14px 16px',
              fontSize: 15,
              fontWeight: 600,
              color: '#FFFFFF',
              background:
                selected.size === 0 && !freeText.trim()
                  ? '#E4E4E7'
                  : '#E11D48',
              border: 'none',
              borderRadius: 6,
              cursor:
                selected.size === 0 && !freeText.trim()
                  ? 'default'
                  : 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (selected.size > 0 || freeText.trim()) {
                e.currentTarget.style.background = '#BE123C';
              }
            }}
            onMouseLeave={(e) => {
              if (selected.size > 0 || freeText.trim()) {
                e.currentTarget.style.background = '#E11D48';
              }
            }}
          >
            Continue{selected.size > 0 ? ` (${selected.size} selected)` : ''}
          </button>
        </div>

        {/* Disclaimer */}
        <p
          style={{
            fontSize: 13,
            color: '#52525B',
            textAlign: 'center',
            lineHeight: 1.5,
            margin: 0,
            padding: '0 8px',
          }}
        >
          Your medication list is checked against our safety rules before any
          protocol is generated. This is not a substitute for professional
          medical advice.
        </p>
      </div>
    </div>
  );
}
